<template>
    <form @submit.prevent="sign" class="on-screen-sign mt-4">
        <div v-if="isLocalhost" class="my-5">
            <h6>DEBUG</h6>
            <p>Item ID: {{itemId}}</p>
        </div>

        <p v-if="itemDescription" class="my-3">
          <small class="text-muted font-italic" v-html="itemDescriptionWithBr"></small>
        </p>

        <div class="padContainer mb-4">
            <vue-signature-pad :options="{ onBegin: onBeginStroke, onEnd: onEndStroke }" ref="signaturePad" />
        </div>

        <div v-if="!authenticating">
            <button type="submit" class="signatureComponentSignButton btn btn-secondary mb-3" :disabled="!hasData">
                <i class="fas fa-paper-plane mr-1"></i> {{ signatureButtonLabel }}
            </button>

            <button class="btn" :disabled="!hasImageData" @click.prevent="clear">
                <i class="fas fa-undo"></i>
            </button>
        </div>
        <button v-else type="button" class="signatureComponentSignButton btn btn-primary mt-3 wait">
            <div class="spinner-border spinner-border-sm mr-2"></div> <i class="fas fa-paper-plane"></i>
        </button>
    </form>
</template>

<style lang="scss" scoped>

.padContainer {
    background-color: #faf8f8;
    position: relative;
    width: 100%;
    height: 150px;

    &:after {
        content: '';
        position: absolute;
        bottom: 20px;
        left: 20px;
        right: 20px;
        height: 1px;
        background-color: #d3d1d1;
    }
}

button {
    display: block;
    margin-left: auto;
    margin-right: auto;
    width: 33%;

    &.wait .spinner-border {
        vertical-align: middle;
    }
}

</style>

<style lang="scss">
  .modal-body .card-body form.on-screen-sign .padContainer {
    width: calc(100% + 20px + 20px);
    margin-left: -20px;
  }
</style>

<script setup>
import axios from "axios";
import { computed, ref } from "vue";

// Props should be in sync with bankIdSign.vue
const props = defineProps({
    itemId: { type: String, default: undefined },
    itemDescription: { type: String, default: undefined },
    appUrl: { type: String, default: undefined },
    redirectOnSignedUrl: { type: String, default: undefined },
    signeeName: { type: String, default: null },
    signatureButtonLabel: { type: String, default: undefined },
});
const emit = defineEmits(["signed"]);

const signaturePad = ref(null);

const authenticating = ref(false);
const hasData = ref(false);
const hasImageData = ref(false);

const isLocalhost = computed(() => document.location.hostname === "localhost");
const itemDescriptionWithBr = computed(() =>
    props.itemDescription ? props.itemDescription.replaceAll("\n", "<br>") : null
);

async function sign() {
    const { isEmpty, data } = signaturePad.value.saveSignature();

    if (isEmpty) {
        updateHasData();
        return;
    }

    try {
        authenticating.value = true;
        await axios.post("/on_screen_signatures", {
            item_id: props.itemId,
            item_description: props.itemDescription,
            data,
        });

        if (props.redirectOnSignedUrl) {
            window.location.replace(props.redirectOnSignedUrl);
        } else {
            emit("signed");
        }
    } catch (error) {
        authenticating.value = false;
        console.warn("Failed to store signature..", error);
    }
}

function clear() {
    signaturePad.value.clearSignature();
    updateHasData();
}

function onBeginStroke() {
    // Needed when we use this in a modal
    signaturePad.value.resizeCanvas();
    updateHasData();
}

function onEndStroke() {
    updateHasData();
}

function updateHasData() {
    hasImageData.value = !signaturePad.value.isEmpty();
    hasData.value = hasImageData.value;
}
</script>
